import React, { useContext, useEffect, useState } from "react";
import { SubscriptionEvents } from "@glamcorner/gc-types/generated/ipcBusEvents";
import { GcSessionInfo, ProfileWithSubscription } from "@glamcorner/gc-types/generated/types";
import { BusProxy } from "@glamcorner/core-cf-lib/lib/client/BusProxy";
import { useSession } from "../../../common/context/SessionProvider";
import { SUBSCRIPTION_CUSTOMER_GROUP_PREFIX } from "../../../common/types";
import ServerGateway from "../../ServerGateway";

// COPY-PASTA from subscription app but simplified

export interface ProfileContextValue {
  profile: ProfileWithSubscription | null;
  error: Error | null;
}

export const ProfileContext = React.createContext<ProfileContextValue>({
  profile: null,
  error: null,
});

export const ProfileProvider: React.FC = ({ children }) => {
  const { gcSession } = useSession();

  const [profile, setProfile] = useState<ProfileWithSubscription | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const serverGateway = new ServerGateway();

  const loadProfile = async (): Promise<void> => {
    try {
      const response = await serverGateway.fetchProfile();
      setProfile(response);
    } catch (err) {
      console.error(err);
      window["airbrake"]?.notify(err);

      BusProxy.instance().broadcast(SubscriptionEvents.FetchCustomerSubscriptionFailed, {
        error: JSON.stringify(err),
      });

      setError(err);
      setProfile(null);
    }
  };

  const isSubscriptionCustomerSession = (gcSessionPayload: GcSessionInfo): boolean => {
    const customerGroup = gcSessionPayload?.customerInfo?.customerGroup;

    if (customerGroup) {
      return customerGroup.includes(SUBSCRIPTION_CUSTOMER_GROUP_PREFIX);
    }
    return false;
  };

  const loadProfilesIfLoggedIn = async (gcSessionPayload: GcSessionInfo) => {
    const isValidSubscriptionSession = isSubscriptionCustomerSession(gcSessionPayload);

    if (isValidSubscriptionSession) {
      await loadProfile();
    }
  };

  useEffect(() => {
    if (gcSession) {
      loadProfilesIfLoggedIn(gcSession);
    }
  }, [gcSession]);

  const contextValue: ProfileContextValue = {
    profile,
    error,
  };
  return <ProfileContext.Provider value={contextValue} children={children} />;
};

export const useProfile = () => useContext(ProfileContext);
